import React, { Fragment, useEffect } from 'react';
import get from 'lodash/get';
import Helmet from 'react-helmet';

import { useSegmentClient } from '@redbubble/react-segment';
import PageSection from '@redbubble/design-system/react/PageSection';
import * as constants from '@redbubble/design-system/react/constants';
import { useSessionContext } from '@redbubble/boom-session-context';

import RecentlyViewedCarousel from '../RecentlyViewedCarousel';
import TopicProductCarouselCollection from '../TopicProductCarouselCollection';
import RecommendedWorksPreviewCarousel from '../RecommendedWorksPreviewCarousel';
import ArtistRecommendations from '../ArtistRecommendations';
import ExploreTabs from '../ExploreTabs';
import { useAnalyticsActions } from '../../containers/redux/withAnalytics';
import { useBrowserInfo } from '../../containers/redux/withBrowserInfo';
import { EXPLORE_FEED_LABEL, analyticsPayload } from '../../lib/analytics';
import useSendImpressions from '../../hooks/useSendImpressions';

export const ExploreForYou = ({ intl, userInfo }) => {
  const browser = useBrowserInfo();
  const { logEvent, addImpression } = useAnalyticsActions();
  const segmentClient = useSegmentClient();

  const sessionContext = useSessionContext();
  const isBot = get(sessionContext, 'isBot', false);

  useEffect(() => {
    const { currency, locale, country } = userInfo;
    segmentClient.page('Explore', { currency, locale, country });
  }, []);

  useSendImpressions();
  const locale = get(userInfo, 'locale', 'en');
  const desktop = !!browser.is.large;
  const profile = desktop ? constants.DESKTOP : constants.MOBILE;

  const logComponentInView = (componentName, loading) => () => {
    logEvent({
      analytics: analyticsPayload.componentShowedInExplorePage(
        componentName,
        loading
      )
    });
  };

  const padding = desktop ? 2 : 1;

  const pageSectionProps = {
    backgroundColor: constants.COLOR_WHITE,
    profile,
    padding,
    size: constants.MEDIUM
  };

  return (
    <Fragment>
      <Helmet>
        <meta name="robots" content="noindex" />
      </Helmet>
      <PageSection {...pageSectionProps}>
        <ExploreTabs locale={locale} currentPath="/explore/for-you" />
        <RecentlyViewedCarousel
          userInfo={userInfo}
          profile={profile}
          logEvent={logEvent}
          addImpression={addImpression}
          logComponentInView={logComponentInView}
          urlRef={isBot ? null : 'explore-for-you-recently-viewed'}
        />
        <RecommendedWorksPreviewCarousel
          userInfo={userInfo}
          profile={profile}
          browser={browser}
          intl={intl}
          logEvent={logEvent}
          logComponentInView={logComponentInView}
          addImpression={addImpression}
          urlRef={isBot ? null : 'explore-feed-preview-carousel'}
        />
        <ArtistRecommendations
          analyticsLabel={EXPLORE_FEED_LABEL}
          workImageWidth={500}
          limit={24}
          onContentShowed={logComponentInView('artist-recommendations', false)}
          profile={profile}
          browser={browser}
          userInfo={userInfo}
          transparentCards
          urlRef={isBot ? null : 'explore-for-you-artist'}
          addImpression={addImpression}
        />
        <TopicProductCarouselCollection
          userInfo={userInfo}
          profile={profile}
          logEvent={logEvent}
          addImpression={addImpression}
          intl={intl}
          logComponentInView={logComponentInView}
          transparentCards
          topicLimit={3}
          urlRef={isBot ? null : 'explore-for-you-topic-product'}
        />
      </PageSection>
    </Fragment>
  );
};
